import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/shared/ProtectedRoute';
import PinLockScreen from './components/PinLockScreen';
import { usePinLock } from './hooks/usePinLock';

// Non-lazy: tiny shared components always needed
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';

// Auth — lazy
const SetPassword    = lazy(() => import('./pages/auth/SetPassword'));
const ResetPassword  = lazy(() => import('./pages/ResetPassword'));
const AuthCallback   = lazy(() => import('./pages/auth/AuthCallback'));

// Public — lazy
const Privacy        = lazy(() => import('./pages/Privacy'));
const Terms          = lazy(() => import('./pages/Terms'));
const VolunteerSignup = lazy(() => import('./pages/volunteer/Signup'));

// Officer — lazy
const OfficerDashboard = lazy(() => import('./pages/officer/Dashboard'));
const OfficerShifts    = lazy(() => import('./pages/officer/Shifts'));
const OfficerProfile   = lazy(() => import('./pages/officer/Profile'));
const Onboarding       = lazy(() => import('./pages/officer/Onboarding'));
const OfficerPending   = lazy(() => import('./pages/officer/Pending'));
const OfficerHistory   = lazy(() => import('./pages/officer/History'));
const OfficerIncidentReport = lazy(() => import('./pages/officer/IncidentReport'));
const OfficerCheckin   = lazy(() => import('./pages/officer/Checkin'));
const OfficerCalendar  = lazy(() => import('./pages/officer/Calendar'));

// Admin — lazy
const AdminDashboard  = lazy(() => import('./pages/admin/Dashboard'));
const AdminGuards     = lazy(() => import('./pages/admin/Guards'));
const AdminLocations  = lazy(() => import('./pages/admin/Locations'));
const AdminShifts     = lazy(() => import('./pages/admin/Shifts'));
const AdminPayroll    = lazy(() => import('./pages/admin/Payroll'));
// const AdminBilling = lazy(() => import('./pages/admin/Billing')); // removed — replaced by invoices system
const AdminHistory    = lazy(() => import('./pages/admin/History'));
const AdminManagers   = lazy(() => import('./pages/admin/Managers'));
const AdminCalendar   = lazy(() => import('./pages/admin/Calendar'));
const AdminClients    = lazy(() => import('./pages/admin/Clients'));
const AdminIncidents  = lazy(() => import('./pages/admin/Incidents'));
const AdminSettings   = lazy(() => import('./pages/admin/Settings'));
const AdminInvoices   = lazy(() => import('./pages/admin/Invoices'));
const AdminInvoiceReview = lazy(() => import('./pages/admin/InvoiceReview'));

// Client portal — lazy
const ClientOnboarding = lazy(() => import('./pages/client/Onboarding'));
const ClientPortal     = lazy(() => import('./pages/client/Portal'));
const ClientInvoices   = lazy(() => import('./pages/client/Invoices'));

// Manager — lazy
const ManagerDashboard = lazy(() => import('./pages/manager/Dashboard'));

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '100vh', background: '#0d0d1a',
          gap: '1rem', padding: '2rem',
        }}>
          <div style={{ color: '#ef4444', fontWeight: 700, fontSize: '1rem' }}>
            Failed to load page
          </div>
          <div style={{ color: '#a0a8c0', fontSize: '0.875rem', textAlign: 'center' }}>
            Try refreshing. If the problem persists, contact support.
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '0.5rem', padding: '0.625rem 1.5rem',
              background: '#4f8ef7', color: '#fff', border: 'none',
              borderRadius: 6, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PageLoader = (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0d0d1a' }}>
    <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading…</div>
  </div>
);

function PinGuard({ children }) {
  const { profile } = useAuth();
  const pinEnabled = !!profile?.pin_enabled && !!profile?.pin_hash;
  const { isLocked, unlock } = usePinLock({ enabled: pinEnabled });

  if (isLocked && pinEnabled) {
    return (
      <PinLockScreen
        pinHash={profile.pin_hash}
        userId={profile.id}
        onUnlock={unlock}
      />
    );
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PinGuard>
        <AppErrorBoundary>
        <Suspense fallback={PageLoader}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/volunteer-signup" element={<VolunteerSignup />} />

          {/* Officer onboarding (auth required, role doesn't matter yet) */}
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />

          {/* Officer pending screen */}
          <Route path="/officer/pending" element={
            <ProtectedRoute role="officer">
              <OfficerPending />
            </ProtectedRoute>
          } />

          {/* Officer routes */}
          <Route path="/officer/dashboard" element={
            <ProtectedRoute role="officer">
              <OfficerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/officer/shifts" element={
            <ProtectedRoute role="officer">
              <OfficerShifts />
            </ProtectedRoute>
          } />
          <Route path="/officer/profile" element={
            <ProtectedRoute role="officer">
              <OfficerProfile />
            </ProtectedRoute>
          } />

          {/* Admin routes */}
          <Route path="/admin/dashboard" element={
            <ProtectedRoute role="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/guards" element={
            <ProtectedRoute role="admin">
              <AdminGuards />
            </ProtectedRoute>
          } />
          <Route path="/admin/locations" element={
            <ProtectedRoute role="admin">
              <AdminLocations />
            </ProtectedRoute>
          } />
          <Route path="/admin/shifts" element={
            <ProtectedRoute role="admin">
              <AdminShifts />
            </ProtectedRoute>
          } />
          <Route path="/admin/payroll" element={
            <ProtectedRoute role="admin">
              <AdminPayroll />
            </ProtectedRoute>
          } />
          {/* /admin/billing removed — replaced by /admin/invoices */}
          <Route path="/admin/history" element={
            <ProtectedRoute role="admin">
              <AdminHistory />
            </ProtectedRoute>
          } />
          <Route path="/admin/managers" element={
            <ProtectedRoute role="admin">
              <AdminManagers />
            </ProtectedRoute>
          } />
          <Route path="/admin/calendar" element={
            <ProtectedRoute role="admin">
              <AdminCalendar />
            </ProtectedRoute>
          } />
          <Route path="/admin/clients" element={
            <ProtectedRoute role="admin">
              <AdminClients />
            </ProtectedRoute>
          } />
          <Route path="/admin/incidents" element={
            <ProtectedRoute role="admin">
              <AdminIncidents />
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute role="admin">
              <AdminSettings />
            </ProtectedRoute>
          } />

          {/* Admin — Invoices */}
          <Route path="/admin/invoices" element={
            <ProtectedRoute role="admin">
              <AdminInvoices />
            </ProtectedRoute>
          } />
          <Route path="/admin/invoices/:invoiceId/review" element={
            <ProtectedRoute role="admin">
              <AdminInvoiceReview />
            </ProtectedRoute>
          } />

          {/* Client — Invoices */}
          <Route path="/client/invoices" element={<ClientInvoices />} />

          {/* Manager routes (office_manager role) */}
          <Route path="/manager/dashboard" element={
            <ProtectedRoute role="office_manager">
              <ManagerDashboard />
            </ProtectedRoute>
          } />

          {/* Officer availability — redirected into Calendar */}
          <Route path="/officer/availability" element={<Navigate to="/officer/calendar" replace />} />

          {/* Officer history */}
          <Route path="/officer/history" element={
            <ProtectedRoute role="officer">
              <OfficerHistory />
            </ProtectedRoute>
          } />

          {/* Officer incident report */}
          <Route path="/officer/incident-report" element={
            <ProtectedRoute role="officer">
              <OfficerIncidentReport />
            </ProtectedRoute>
          } />

          {/* Officer GPS check-in */}
          <Route path="/officer/checkin/:shiftId" element={
            <ProtectedRoute role="officer">
              <OfficerCheckin />
            </ProtectedRoute>
          } />

          {/* Officer calendar */}
          <Route path="/officer/calendar" element={
            <ProtectedRoute role="officer">
              <OfficerCalendar />
            </ProtectedRoute>
          } />

          {/* Client routes */}
          <Route path="/client-onboarding" element={<ClientOnboarding />} />
          <Route path="/client-portal" element={
            <ProtectedRoute role="client">
              <ClientPortal />
            </ProtectedRoute>
          } />

          {/* Catch-all → login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Suspense>
        </AppErrorBoundary>
        </PinGuard>
      </BrowserRouter>
    </AuthProvider>
  );
}
